import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

const CATEGORIES = [
 { cat: 'Transform', method: 'map()' },
 { cat: 'Filter', method: 'filter()' },
 { cat: 'Aggregate', method: 'reduce()' },
 { cat: 'Search', method: 'find() / findIndex()' },
 { cat: 'Validate', method: 'some() / every()' },
 { cat: 'Sort', method: 'sort()' },
];

export const JSArrayMethods: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Array Methods</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    The declarative toolkit for collections — map, filter, reduce, find, some, every, sort, flat — that replaces hand-written loops and powers modern JavaScript.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="20 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>An e-commerce API returns a list of products, and the business wants: only available products, total inventory value, a specific product, an &quot;any out of stock?&quot; check, sorting by price, and a UI-shaped transform.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const products = [\n  { id: 1, name: "Laptop", price: 80000, inStock: true },\n  { id: 2, name: "Phone",  price: 40000, inStock: false },\n  { id: 3, name: "Tablet", price: 30000, inStock: true },\n];` }]} defaultLanguage="javascript" id="am-products" />
   <Para>You could do all of it with raw <Code>for</Code> loops, temp variables, and manual bookkeeping — possible, but noisy and bug-prone. Array methods solve these collection problems elegantly.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Array methods</Mark> are built-in functions on arrays that let you search, transform, filter, aggregate, sort, and manipulate collections — specialized tools for processing data.</p>
   </div>
  </section>

  {/* 3. Why */}
  <section className="mb-12">
   <SectionHeading n={3}>Why They Exist</SectionHeading>
   <Para>Compare the imperative loop with the declarative method — same result, far less noise:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// imperative\nconst names = [];\nfor (let i = 0; i < users.length; i++) {\n  names.push(users[i].name);\n}\n\n// declarative\nconst names = users.map(user => user.name);` }]} defaultLanguage="javascript" id="am-why" />
   <div className="flex flex-wrap gap-2">{['Less code', 'More readable', 'Easier maintenance', 'Functional style', 'Fewer bugs'].map((b) => <Pill key={b}>{b}</Pill>)}</div>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea</SectionHeading>
   <Para>Nearly every collection operation falls into one of six categories, each with a go-to method:</Para>
   <div className="my-6 grid grid-cols-2 sm:grid-cols-3 gap-2">
    {CATEGORIES.map((c) => (
     <div key={c.cat} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-500">{c.cat}</div>
      <div className="font-mono text-[13px] font-bold text-amber-600 dark:text-amber-400">{c.method}</div>
     </div>
    ))}
   </div>
  </section>

  {/* 5. map */}
  <section className="mb-12">
   <SectionHeading n={5}>map() — Transform</SectionHeading>
   <Para>Turns every element into something else, returning a <Mark>new array of the same length</Mark>. The original is untouched.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const numbers = [1, 2, 3];\nconst doubled = numbers.map(num => num * 2);\n// [2, 4, 6]   (numbers is still [1, 2, 3])\n\nconst users = [{ name: "Sachin" }, { name: "Rahul" }];\nconst names = users.map(user => user.name); // ["Sachin", "Rahul"]` }]} defaultLanguage="javascript" id="am-map" />
   <pre className="text-[13px] font-mono text-gray-600 dark:text-gray-400">{`1 → 2\n2 → 4\n3 → 6`}</pre>
   <Callout tone="info">Complexity <Code>O(n)</Code>. Use <Code>map</Code> when the output array should have one item per input item.</Callout>
  </section>

  {/* 6. filter */}
  <section className="mb-12">
   <SectionHeading n={6}>filter() — Keep What Matches</SectionHeading>
   <Para>Returns a new array containing only elements for which the callback returns truthy:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const numbers = [1, 2, 3, 4, 5];\nconst evens = numbers.filter(n => n % 2 === 0); // [2, 4]\n\nconst available = products.filter(p => p.inStock); // [Laptop, Tablet]` }]} defaultLanguage="javascript" id="am-filter" />
   <Callout tone="info">Complexity <Code>O(n)</Code>. The result can be shorter (or longer-than-zero) — never transformed, only kept or dropped.</Callout>
  </section>

  {/* 7. find / findIndex */}
  <section className="mb-12">
   <SectionHeading n={7}>find() &amp; findIndex()</SectionHeading>
   <Para><Code>find</Code> returns the <Mark>first matching element</Mark> (or <Code>undefined</Code>); <Code>findIndex</Code> returns its index (or <Code>-1</Code>):</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const users = [{ id: 1 }, { id: 2 }];\n\nusers.find(u => u.id === 2);      // { id: 2 }   (the object)\nusers.findIndex(u => u.id === 2); // 1           (the index)` }]} defaultLanguage="javascript" id="am-find" />
   <Callout tone="warning"><Code>find</Code> returns the element itself, not an array — a common point of confusion vs <Code>filter</Code>.</Callout>
  </section>

  {/* 8. some / every */}
  <section className="mb-12">
   <SectionHeading n={8}>some() &amp; every() — Validate</SectionHeading>
   <Para><Code>some</Code> checks if <Mark>any</Mark> element passes; <Code>every</Code> checks if <Mark>all</Mark> do. Both short-circuit as soon as the answer is known.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `[1, 3, 5, 8].some(n => n % 2 === 0);   // true  (stops at 8)\n[2, 4, 6].every(n => n % 2 === 0);     // true\nproducts.every(p => p.inStock);        // false (Phone is out)` }]} defaultLanguage="javascript" id="am-someevery" />
  </section>

  {/* 9. reduce */}
  <section className="mb-12">
   <SectionHeading n={9}>reduce() — Aggregate</SectionHeading>
   <Para>The most powerful method: collapse an entire array into a <Mark>single value</Mark> by accumulating. Always pass an initial value.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const numbers = [10, 20, 30];\nconst total = numbers.reduce((sum, num) => sum + num, 0);\n// 60` }]} defaultLanguage="javascript" id="am-reduce" />
   <pre className="text-[13px] font-mono text-gray-600 dark:text-gray-400">{`start 0\n 0 + 10 = 10\n10 + 20 = 30\n30 + 30 = 60`}</pre>
   <CodeBlock snippets={[{ language: 'javascript', code: `const order = [{ price: 500 }, { price: 700 }];\nconst total = order.reduce((sum, item) => sum + item.price, 0); // 1200` }]} defaultLanguage="javascript" id="am-reduce2" />
   <Callout tone="warning">Forgetting the initial value is a classic bug — without it, <Code>reduce</Code> uses the first element as the seed, which breaks on empty arrays.</Callout>
  </section>

  {/* 10. sort */}
  <section className="mb-12">
   <SectionHeading n={10}>sort() — Order Elements</SectionHeading>
   <Para>By default <Code>sort</Code> converts items to strings — which gives wrong results for numbers. Always pass a comparator:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `[10, 2, 30].sort();            // [10, 2, 30]  — string sort, wrong!\n[10, 2, 30].sort((a, b) => a - b); // [2, 10, 30]  ascending\n[10, 2, 30].sort((a, b) => b - a); // [30, 10, 2]  descending\n\nproducts.sort((a, b) => a.price - b.price); // by price` }]} defaultLanguage="javascript" id="am-sort" />
   <Callout tone="warning"><Code>sort</Code> <Mark>mutates</Mark> the original array. Clone first (<Code>[...arr].sort(...)</Code>) if you need to keep the original.</Callout>
  </section>

  {/* 11. flat / flatMap */}
  <section className="mb-12">
   <SectionHeading n={11}>flat() &amp; flatMap()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `[[1, 2], [3, 4]].flat();      // [1, 2, 3, 4]\n[1, [2, [3]]].flat(2);        // [1, 2, 3]  (flatten 2 levels)\n\nconst sentences = ["Hello World", "JS Rocks"];\nsentences.flatMap(s => s.split(" "));\n// ["Hello", "World", "JS", "Rocks"]` }]} defaultLanguage="javascript" id="am-flat" />
   <Callout tone="info"><Code>flatMap</Code> = <Code>map</Code> then <Code>flat(1)</Code>, in one efficient pass.</Callout>
  </section>

  {/* 12. Method chaining */}
  <section className="mb-12">
   <SectionHeading n={12}>Method Chaining</SectionHeading>
   <Para>Because the immutable methods return arrays, you can pipe them into a readable data pipeline:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const result = products\n  .filter(p => p.inStock)\n  .map(p => p.name)\n  .sort();` }]} defaultLanguage="javascript" id="am-chain" />
   <pre className="text-[13px] font-mono text-gray-600 dark:text-gray-400">{`products → filter → map → sort → result`}</pre>
  </section>

  {/* 13. Real-world API */}
  <section className="mb-12">
   <SectionHeading n={13}>Real-World API Example</SectionHeading>
   <Para>&quot;Show the names of active users&quot; — filter then map:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const users = [\n  { name: "Sachin", active: true },\n  { name: "Rahul", active: false },\n];\n\nconst activeUsers = users\n  .filter(user => user.active)\n  .map(user => user.name);\n\nconsole.log(activeUsers);` }]} defaultLanguage="javascript" id="am-realworld" />
   <Output>{`["Sachin"]`}</Output>
  </section>

  {/* 14. Performance */}
  <section className="mb-12">
   <SectionHeading n={14}>Performance Considerations</SectionHeading>
   <Para>Each method traverses the array, so each is <Code>O(n)</Code>. A <Code>.filter().map()</Code> chain is two passes — <Code>O(n) + O(n)</Code>, still linear. Perfectly fine for small/medium data; for millions of records, consider a single <Code>reduce</Code> pass or other optimizations.</Para>
  </section>

  {/* 15. Across languages */}
  <section className="mb-12">
   <SectionHeading n={15}>Across Languages</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `interface User { name: string; active: boolean; }\n\nconst users: User[] = [{ name: "Sachin", active: true }];\nconst activeNames = users\n  .filter(u => u.active)\n  .map(u => u.name);` }]} defaultLanguage="typescript" id="am-ts" />
   <CodeBlock snippets={[{ language: 'java', code: `List<Integer> evens = numbers.stream()\n  .filter(n -> n % 2 == 0)\n  .collect(Collectors.toList());` }]} defaultLanguage="java" id="am-java" />
   <Callout tone="info">Java <Mark>Streams</Mark> provide the same map/filter/reduce model.</Callout>
  </section>

  {/* 16. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={16}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="map vs filter">Use <Code>map</Code> to transform, <Code>filter</Code> to keep/drop — not interchangeably.</Callout>
   <Callout tone="warning" title="filter when find fits">If you only need the first match, <Code>find</Code> is clearer and stops early.</Callout>
   <Callout tone="warning" title="reduce without an initial value">Always seed it, especially for possibly-empty arrays.</Callout>
   <Callout tone="warning" title="sort surprises">Default sort is lexicographic and mutating — pass a comparator and clone if needed.</Callout>
   <Callout tone="warning" title="Over-long chains">Huge chains hurt readability; break them up with well-named steps.</Callout>
  </section>

  {/* 17. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={17}>Real Interview Questions</SectionHeading>
   <QA q={<>Difference between <Code>map</Code> and <Code>forEach</Code>?</>}>
    <Code>map</Code> returns a new transformed array; <Code>forEach</Code> returns <Code>undefined</Code> and is used purely for side effects. Use <Code>map</Code> when you need the result.
   </QA>
   <QA q={<>Difference between <Code>filter</Code> and <Code>find</Code>?</>}>
    <Code>filter</Code> returns an array of <em>all</em> matches; <Code>find</Code> returns the <em>first</em> matching element (or <Code>undefined</Code>).
   </QA>
   <QA q={<>Difference between <Code>some</Code> and <Code>every</Code>?</>}>
    <Code>some</Code> is true if at least one element passes; <Code>every</Code> is true only if all pass. Both short-circuit.
   </QA>
   <QA q="Explain reduce with an example.">
    <Code>reduce</Code> folds an array into one value using an accumulator: <Code>[10,20,30].reduce((s,n)=&gt;s+n,0)</Code> → <Code>60</Code>.
   </QA>
   <QA q="Why does sort behave strangely with numbers?">
    Default <Code>sort</Code> converts elements to strings and compares lexicographically, so <Code>[10,2,30]</Code> stays misordered. Pass <Code>(a,b)=&gt;a-b</Code>.
   </QA>
   <QA q={<>Difference between <Code>flat</Code> and <Code>flatMap</Code>?</>}>
    <Code>flat(depth)</Code> only flattens; <Code>flatMap</Code> maps each element then flattens one level — more efficient than <Code>map().flat()</Code>.
   </QA>
  </section>

  {/* 18. Mental model */}
  <section className="mb-12">
   <SectionHeading n={18}>Mental Model</SectionHeading>
   <Para>Think of array methods as a <Mark>factory conveyor belt</Mark>: raw data enters, then flows through stations — filter, transform, aggregate — until it reaches the final output. Each method is one station in the pipeline.</Para>
   <pre className="my-2 text-[13px] font-mono text-gray-600 dark:text-gray-400">{`raw data → filter → map → reduce → final output`}</pre>
  </section>

  {/* 19 & 20 */}
  <section className="mb-4">
   <SectionHeading n={19}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Array methods provide declarative ways to transform, filter, search, aggregate, and process collections without manually managing loops.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    map, filter, reduce, find, some, every, and sort are among the most-used tools in modern JavaScript. Mastering them improves readability, interview performance, and productivity in React, Node.js, and machine-coding rounds.
   </Callout>
  </section>
 </div>
);

export default JSArrayMethods;
